"use client"

import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  Check,
  AlertCircle,
  ArrowLeft,
  FileText,
  Clock,
  Camera,
  Edit,
  Loader,
  Award,
  CheckCircle2,
  CheckCircle,
  XCircle,
} from "lucide-react"
import { getAssignmentData } from "./api"
import { generatePresignedUrl, postStudentAnsFile } from "../postFileAws"
import { postAws } from "../aws"

function decodeJWT(jwtToken) {
  const [header, payload, signature] = jwtToken.split(".")
  const decodedHeader = JSON.parse(atob(header))
  const decodedPayload = JSON.parse(atob(payload))

  return {
    header: decodedHeader,
    payload: decodedPayload,
    signature: signature,
  }
}

export default function AssignmentDetails() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [assignment, setAssignment] = useState(null)
  const [questionarr, setQuestionarr] = useState([])
  const [uploads, setUploads] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [answerMethod, setAnswerMethod] = useState({})
  const [showWarning, setShowWarning] = useState(false)
  const [studentId, setStudentId] = useState()
  const [loadingImageIndex, setLoadingImageIndex] = useState(null)
  const [questionImages, setQuestionImages] = useState({})
  const [activeQuestion, setActiveQuestion] = useState(0)
  const [totalMarks, setTotalMarks] = useState(0)
  const [dueDate, setDueDate] = useState("No due date")
  const organisationId = localStorage.getItem("organisationId")

  useEffect(() => {
    const fetchAssignment = async () => {
      try {
        const response = await getAssignmentData(id)
        setAssignment(response)

        if (response.assignmentQuestion) {
          setQuestionarr(response.assignmentQuestion)

          // Calculate total marks
          const total = response.assignmentQuestion.reduce((sum, question) => {
            return sum + (question.maxMarks || 0)
          }, 0)
          setTotalMarks(total)
        }

        // Format due date
        if (response.assignmentDueDate) {
          const date = new Date(response.assignmentDueDate)
          if (!isNaN(date.getTime())) {
            setDueDate(
              date.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              }),
            )
          }
        }

        getTokenDetailsFromLocalStorage()

        // Fetch presigned URLs for question images
        const imageUrls = {}
        for (const question of response.assignmentQuestion) {
          if (question.questionImageUrl) {
            try {
              const url = await generatePresignedUrl(question.questionImageUrl)
              imageUrls[question.questionImageUrl] = url
            } catch (error) {
              console.error("Error generating presigned URL:", error)
            }
          }
        }
        setQuestionImages(imageUrls)
      } catch (error) {
        console.error("Error fetching assignment:", error)
      }
    }

    function getTokenDetailsFromLocalStorage() {
      const jwtToken = localStorage.getItem("token")
      if (jwtToken) {
        const tokenDetails = decodeJWT(jwtToken)
        setStudentId(tokenDetails.payload.user.id)
      } else {
        console.log("No token found in localStorage")
      }
    }

    fetchAssignment()
  }, [id])

  const handleFileUpload = (questionId, file) => {
    setUploads((prev) => ({
      ...prev,
      [questionId]: file,
    }))
    setAnswerMethod((prev) => ({
      ...prev,
      [questionId]: "pdf",
    }))
  }

  const handleTextAnswer = (index, value) => {
    const updatedQuestions = [...questionarr]
    updatedQuestions[index].user_answer = value
    setQuestionarr(updatedQuestions)
  }

  const handleMCQAnswer = (index, selectedOption) => {
    const updatedQuestions = [...questionarr]
    updatedQuestions[index].user_answer = selectedOption
    setQuestionarr(updatedQuestions)
  }

  const handlePhotoCapture = async (index, file) => {
    if (!file) {
      console.log("No file selected")
      return
    }

    setLoadingImageIndex(index)

    try {
      const studentId = localStorage.getItem("studentId")
      const response = await postStudentAnsFile(file, studentId, id, `question${index}`)
      const uploadedUrl = await generatePresignedUrl(response)
      const updatedQuestions = [...questionarr]
      updatedQuestions[index].user_image = response
      updatedQuestions[index].user_displayImage = uploadedUrl
      setQuestionarr(updatedQuestions)
    } catch (error) {
      console.error(error)
    } finally {
      setLoadingImageIndex(null)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    const studentId = localStorage.getItem("studentId")
    const classRoomId=localStorage.getItem("classRoomId")
    const submission = {
      body: {
        assignmentId: assignment._id,
        teacherId: assignment.teacherId,
        assignmentQuestion: questionarr,
        studentId: studentId,
        organisationId: organisationId,
        classRoomId: classRoomId,
        status: "saved",
      },
      request: "save-Assignment",
    }

    try {
      const response = await postAws("https://api-dev.aurait.ai/submission", "POST", submission)
      setTimeout(() => {
        setSaving(false)
        navigate("/assignments")
      }, 3000)
    } catch (err) {
      console.error(`Couldn't submit assignment: ${err}`)
      setSaving(false)
    }
  }

  const handleSubmit = async () => {
    const unansweredQuestions = questionarr.filter((_, index) => !isQuestionAnswered(index)).length
    if (unansweredQuestions > 0) {
      setShowWarning(true)
    } else {
      submitAssignment()
    }
  }

  const submitAssignment = async () => {
    setSubmitting(true)
    const studentId = localStorage.getItem("studentId")
       const classRoomId=localStorage.getItem("classRoomId")
    const submission = {
      body: {
        assignmentId: assignment._id,
        teacherId: assignment.teacherId,
        assignmentQuestion: questionarr,
        studentId: studentId,
        organisationId: organisationId,
         classRoomId: classRoomId,
        status: "submitted",
      },
      request: "submit-Assignment",
    }

    try {
      const response = await postAws("https://api-dev.aurait.ai/submission", "POST", submission)
      setTimeout(() => {
        setSubmitting(false)
        navigate("/assignments")
      }, 3000)
    } catch (err) {
      console.error(`Couldn't submit assignment: ${err}`)
      setSubmitting(false)
    }
  }

  const isQuestionAnswered = (questionId) => {
    const question = questionarr[questionId]
    return (
      answerMethod[questionId] === "pdf" ||
      question.user_answer ||
      question.user_image ||
      question.user_displayImage ||
      answerMethod[questionId] === "photo" ||
      (question.mcqOptions && question.mcqOptions.length > 0 && question.user_answer)
    )
  }

  const getCompletionStatus = () => {
    if (!questionarr.length) return 0
    const answeredCount = questionarr.filter((_, index) => isQuestionAnswered(index)).length
    return Math.round((answeredCount / questionarr.length) * 100)
  }

  const getCompletedQuestionsCount = () => {
    return questionarr.filter((_, index) => isQuestionAnswered(index)).length
  }

  const getQuestionStats = () => {
    const total = questionarr.length
    const attempted = getCompletedQuestionsCount()
    const notDone = total - attempted
    return { total, attempted, notDone }
  }

  const formatDueDate = (dateString) => {
    if (!dateString || dateString === "No due date") return "No due date"

    const date = new Date(dateString)
    if (isNaN(date.getTime())) return "No due date"

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  if (!assignment) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-lg text-indigo-800 font-medium">Loading assignment...</p>
        </div>
      </div>
    )
  }

  const currentQuestion = questionarr[activeQuestion]
  const marks = currentQuestion.maxMarks || 0
  const questionStats = getQuestionStats()

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 -mt-6">
      <div className="container mx-auto px-2 md:px-4 py-0 max-w-7xl">
        {/* Main Content Card */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-md overflow-hidden mt-6 md:mt-8">
          {/* Assignment Header - Single Row */}
          <div
            className="bg-gradient-to-r from-indigo-500 to-blue-600 px-4 md:px-4"
            style={{ paddingTop: "1.375rem", paddingBottom: "1.375rem" }}
          >
            {/* Mobile Layout */}
            <div className="block lg:hidden">
              {/* Row 1: Back button at top */}
              <div className="flex items-center mb-3">
                <button
                  onClick={() => navigate("/assignments")}
                  className="flex items-center text-white hover:text-indigo-100 transition-colors duration-200"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  <span className="text-sm font-medium">Back</span>
                </button>
              </div>

              {/* Row 2: Stats below back button */}
              <div className="flex items-center justify-start gap-1 mb-3">
                <div className="inline-flex items-center gap-1 border border-white/30 backdrop-blur-sm rounded-md px-2 py-1 text-white text-xs font-medium bg-white/10">
                  <FileText className="w-3 h-3 opacity-80" />
                  <span>
                    Total: <span className="font-bold">{questionStats.total}</span>
                  </span>
                </div>
                <div className="inline-flex items-center gap-1 border border-white/30 backdrop-blur-sm rounded-md px-2 py-1 text-white text-xs font-medium bg-white/10">
                  <CheckCircle className="w-3 h-3 opacity-100" />
                  <span>
                    Attempted: <span className="font-bold">{questionStats.attempted}</span>
                  </span>
                </div>
                <div className="inline-flex items-center gap-1 border border-white/30 backdrop-blur-sm rounded-md px-2 py-1 text-white text-xs font-medium bg-white/10">
                  <XCircle className="w-3 h-3 opacity-80" />
                  <span>
                    Not Done: <span className="font-bold">{questionStats.notDone}</span>
                  </span>
                </div>
              </div>

              {/* Row 3: Title centered */}
              <div className="text-center mb-3">
                <h1 className="text-base font-bold text-white">
                  {assignment.assignmentTitle
                    ? assignment.assignmentTitle.charAt(0).toUpperCase() + assignment.assignmentTitle.slice(1)
                    : assignment.assignment_topic
                      ? assignment.assignment_topic.charAt(0).toUpperCase() + assignment.assignment_topic.slice(1)
                      : ""}
                </h1>
              </div>

              {/* Row 4: Due date and marks centered */}
              <div className="flex items-center justify-center gap-2 mb-2">
                <div className="flex items-center bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full border border-white/30">
                  <Clock className="w-3 h-3 mr-1" />
                  <span className="text-xs text-white">Due: {dueDate}</span>
                </div>
                <div className="flex items-center bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full border border-white/30">
                  <Award className="w-3 h-3 mr-1" />
                  <span className="text-xs font-medium text-white">{totalMarks} Marks</span>
                </div>
              </div>

              {/* Row 5: Progress */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-6 h-6 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center mr-2">
                    <span className="text-white text-xs font-bold">{activeQuestion + 1}</span>
                  </div>
                  <span className="text-white text-xs">
                    Question {activeQuestion + 1} of {questionarr.length}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="text-white text-xs mr-2">
                    {getCompletedQuestionsCount()}/{questionarr.length}
                  </span>
                  <div className="w-16 bg-white/20 rounded-full h-1.5 mr-1">
                    <div
                      className="bg-white h-1.5 rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${getCompletionStatus()}%` }}
                    ></div>
                  </div>
                  <span className="text-white text-xs">{getCompletionStatus()}%</span>
                </div>
              </div>
            </div>

            {/* Desktop Layout - Restructured */}
<div className="hidden lg:block">
  {/* Back Button */}
  <div className="flex items-center mb-3">
    <button
      onClick={() => navigate("/assignments")}
      className="flex items-center text-white hover:text-indigo-100 transition-colors duration-200"
    >
      <ArrowLeft className="w-4 h-4 mr-2" />
      <span className="text-sm font-medium">Back</span>
    </button>
  </div>

  {/* Unified Row: Stats, Title, Due Date, Marks */}
  <div className="flex items-center justify-between mb-3">
    {/* Stats (Left) */}
    <div className="flex items-center gap-2">
      <div className="inline-flex items-center gap-1 border border-white/30 backdrop-blur-sm rounded-md px-2 py-1 text-white text-xs font-medium bg-white/10">
        <FileText className="w-3 h-3 opacity-80" />
        <span>
          Total: <span className="font-bold">{questionStats.total}</span>
        </span>
      </div>
      <div className="inline-flex items-center gap-1 border border-white/30 backdrop-blur-sm rounded-md px-2 py-1 text-white text-xs font-medium bg-white/10">
        <CheckCircle className="w-3 h-3 opacity-100" />
        <span>
          Attempted: <span className="font-bold">{questionStats.attempted}</span>
        </span>
      </div>
      <div className="inline-flex items-center gap-1 border border-white/30 backdrop-blur-sm rounded-md px-2 py-1 text-white text-xs font-medium bg-white/10">
        <XCircle className="w-3 h-3 opacity-80" />
        <span>
          Not Done: <span className="font-bold">{questionStats.notDone}</span>
        </span>
      </div>
    </div>

    {/* Title (Center) */}
    <div className="flex-shrink-0">
      <h1 className="text-base font-bold text-white text-center whitespace-nowrap">
        {assignment.assignmentTitle
          ? assignment.assignmentTitle.charAt(0).toUpperCase() + assignment.assignmentTitle.slice(1)
          : assignment.assignment_topic
            ? assignment.assignment_topic.charAt(0).toUpperCase() + assignment.assignment_topic.slice(1)
            : ""}
      </h1>
    </div>

    {/* Due & Marks (Right) */}
    <div className="flex items-center gap-2 justify-end">
      <div className="flex items-center bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full border border-white/30">
        <Clock className="w-3 h-3 mr-1" />
        <span className="text-xs text-white">Due: {dueDate}</span>
      </div>
      <div className="flex items-center bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full border border-white/30">
        <Award className="w-3 h-3 mr-1" />
        <span className="text-xs font-medium text-white">{totalMarks} Marks</span>
      </div>
    </div>
  </div>
</div>

          </div>

          {/* Question Section */}
          <div className="p-4">
            {/* Question Navigation */}
            <div className="overflow-x-auto py-2 mb-4">
              <div className="flex gap-3">
                {questionarr.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveQuestion(index)}
                    className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-200 ${
                      activeQuestion === index
                        ? "bg-indigo-600 text-white shadow-md"
                        : isQuestionAnswered(index)
                          ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                          : "bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200"
                    }`}
                  >
                    {isQuestionAnswered(index) ? <CheckCircle2 className="w-5 h-5" /> : index + 1}
                  </button>
                ))}
                {/* Progress with circular indicator and progress bar */}
                <div className="flex items-center ml-auto mr-10">
                  <span className="text-xs mr-2">
                    {getCompletedQuestionsCount()}/{questionarr.length}
                  </span>
                  <div className="w-24 rounded-full h-1.5 mr-1 bg-gray-200">
                    <div
                      className="h-1.5 rounded-full transition-all duration-500 ease-out bg-blue-500"
                      style={{ width: `${getCompletionStatus()}%` }}
                    ></div>
                  </div>
                  <span className="text-xs">{getCompletionStatus()}%</span>
                </div>
              </div>
            </div>

            {/* Current Question */}
            <div className="bg-white rounded-2xl shadow-sm p-4 mb-4 border border-gray-100">
              <div className="flex items-start mb-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-3 bg-indigo-100 text-indigo-600">
                  {activeQuestion + 1}
                </div>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h2 className="text-base font-medium text-gray-800">Question</h2>
                    <span className="px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-medium">
                      {marks} {marks === 1 ? "Mark" : "Marks"}
                    </span>
                    <span className="px-3 py-1 rounded-full bg-cyan-100 text-cyan-700 text-xs font-medium">
                      {currentQuestion.questionType ||
                        (currentQuestion.mcqOptions && currentQuestion.mcqOptions.length > 0
                          ? "Multiple Choice"
                          : "Text Answer")}
                    </span>
                  </div>
                  <div className="text-sm text-gray-700 mb-4">
                    {currentQuestion.question_text || currentQuestion.question}
                  </div>
                </div>
              </div>

              {currentQuestion.questionImageUrl && (
                <div className="mb-4 overflow-hidden rounded-lg shadow-sm">
                  <img
                    src={questionImages[currentQuestion.questionImageUrl] || currentQuestion.questionImageUrl}
                    alt="Question Image"
                    className="w-full h-auto object-cover"
                    onError={(e) => {
                      e.target.onerror = null
                      e.target.src = "/placeholder.svg"
                    }}
                  />
                </div>
              )}

              <div className="mt-4 space-y-3">
                {currentQuestion.mcqOptions && currentQuestion.mcqOptions.length > 0 ? (
                  <div className="space-y-2">
                    {currentQuestion.mcqOptions.map((option, optionIndex) => (
                      <div
                        key={optionIndex}
                        onClick={() => handleMCQAnswer(activeQuestion, option)}
                        className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all duration-200 ${
                          currentQuestion.user_answer === option
                            ? "bg-indigo-50 border-indigo-300"
                            : "border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        <div
                          className={`w-5 h-5 rounded-full mr-3 flex items-center justify-center border ${
                            currentQuestion.user_answer === option
                              ? "border-indigo-500 bg-indigo-500"
                              : "border-gray-400"
                          }`}
                        >
                          {currentQuestion.user_answer === option && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <label
                          htmlFor={`question-${activeQuestion}-option-${optionIndex}`}
                          className="text-sm flex-1 cursor-pointer"
                        >
                          {option}
                        </label>
                        <input
                          type="radio"
                          id={`question-${activeQuestion}-option-${optionIndex}`}
                          name={`question-${activeQuestion}`}
                          value={option}
                          checked={currentQuestion.user_answer === option}
                          onChange={() => handleMCQAnswer(activeQuestion, option)}
                          className="sr-only"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <textarea
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 min-h-[100px] text-sm"
                        rows="4"
                        value={currentQuestion.user_answer || ""}
                        onChange={(e) => handleTextAnswer(activeQuestion, e.target.value)}
                        placeholder="Type your answer here..."
                      ></textarea>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="flex items-center justify-center px-3 py-2 border border-indigo-300 rounded-lg text-indigo-600 bg-white hover:bg-indigo-50 transition-all duration-200 relative cursor-pointer group">
                        <Camera className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-200" />
                        <span className="font-medium">Upload Image</span>
                        <input
                          onChange={(e) => handlePhotoCapture(activeQuestion, e.target.files[0])}
                          className="opacity-0 absolute h-full w-full top-0 left-0 cursor-pointer"
                          type="file"
                          accept="image/*"
                        />
                      </div>
                    </div>
                  </>
                )}
                <div className="flex">
                  {loadingImageIndex === activeQuestion ? (
                    <div className="flex justify-center items-center mt-2">
                      <Loader className="animate-spin text-indigo-500 w-6 h-6" />
                      <span className="ml-2 text-indigo-500">Uploading image...</span>
                    </div>
                  ) : currentQuestion.user_displayImage ? (
                    <div className="mt-4 relative group">
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-xl transition-all duration-200 flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <Edit className="w-8 h-8 text-white" />
                        </div>
                      </div>
                      <img
                        src={currentQuestion.user_displayImage || "/placeholder.svg"}
                        alt="Uploaded Answer"
                        className="w-40 h-auto rounded-xl shadow-md border-2 border-indigo-100"
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-4 gap-2 mb-6">
              <button
                onClick={() => activeQuestion > 0 && setActiveQuestion(activeQuestion - 1)}
                disabled={activeQuestion === 0}
                className={`px-2 py-2 rounded-lg flex items-center justify-center ${
                  activeQuestion === 0
                    ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                    : "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                }`}
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline ml-1">Previous</span>
              </button>

              <button
                onClick={handleSave}
                disabled={saving}
                className={`px-2 py-2 rounded-lg flex items-center justify-center ${
                  saving ? "bg-gray-400 cursor-not-allowed" : "bg-green-600 text-white hover:bg-green-700"
                }`}
              >
                {saving ? (
                  <>
                    <Loader className="animate-spin w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Saving...</span>
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Save</span>
                  </>
                )}
              </button>

              <button
                onClick={handleSubmit}
                disabled={submitting}
                className={`px-2 py-2 rounded-lg flex items-center justify-center ${
                  submitting ? "bg-gray-400 cursor-not-allowed" : "bg-indigo-600 text-white hover:bg-indigo-700"
                }`}
              >
                {submitting ? (
                  <>
                    <Loader className="animate-spin w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Submitting...</span>
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Submit</span>
                  </>
                )}
              </button>

              <button
                onClick={() => activeQuestion < questionarr.length - 1 && setActiveQuestion(activeQuestion + 1)}
                disabled={activeQuestion === questionarr.length - 1}
                className={`px-2 py-2 rounded-lg flex items-center justify-center ${
                  activeQuestion === questionarr.length - 1
                    ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                    : "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                }`}
              >
                <span className="hidden sm:inline">Next</span>
                <ArrowLeft className="w-4 h-4 sm:ml-2 transform rotate-180" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Warning Modal */}
      {showWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 md:p-8 rounded-2xl max-w-md w-full shadow-2xl transform transition-all duration-300 animate-fadeIn">
            <div className="flex items-center text-amber-500 mb-4">
              <AlertCircle className="w-8 h-8 mr-3" />
              <h2 className="text-xl font-bold text-gray-800">Incomplete Submission</h2>
            </div>
            <p className="mb-6 text-gray-700">
              You have not attempted all questions. Are you sure you want to submit your assignment?
            </p>
            <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4">
              <button
                onClick={() => setShowWarning(false)}
                className="px-5 py-2.5 bg-gray-100 text-gray-800 rounded-xl hover:bg-gray-200 font-medium transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowWarning(false)
                  submitAssignment()
                }}
                className="px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-xl hover:from-indigo-700 hover:to-blue-700 font-medium transition-colors duration-200 shadow-md"
              >
                Submit Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
